import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, CheckCircle, Cpu, Settings, Shield, Wrench, Layers, Network, Activity, Gauge, TrendingUp, Eye, SlidersHorizontal, BarChart3, Plug, ClipboardList, Wallet, Maximize2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import electricalHero from "@/assets/services/electrical-controls.jpg";
import electricalBenefitsImg from "@/assets/generated/electrical-automation-controls.jpg?w=760&format=webp&quality=72";
import integrationImg from "@/assets/generated/manufacturing-floor.webp?w=760&format=webp&quality=72";
import { RegionalSEO } from "@/components/seo/RegionalSEO";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import InternalLinks from "@/components/seo/InternalLinks";
import SoftwareControls from "@/components/marketing/SoftwareControls";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ServiceCallbackBand from "@/components/marketing/ServiceCallbackBand";

const highlights = [
  "Nationwide service availability",
  "Vendor network strength",
  "Scalable design automation",
  "Long-term client partnerships",
  "Experienced professionals",
  "Rapid turnaround time",
];

const valueProps = [
  {
    icon: Settings,
    title: "Turnkey Execution",
    description: "Manage full project delivery from design to commissioning under one system.",
  },
  {
    icon: Cpu,
    title: "Industrial Focus",
    description: "Build automation systems tailored for manufacturing, warehousing, and logistics.",
  },
  {
    icon: Layers,
    title: "End-to-End Delivery",
    description: "Handle design, installation, and commissioning under one contract.",
  },
  {
    icon: Shield,
    title: "Safety First Approach",
    description: "Follow strict industrial safety and compliance standards.",
  },
];

const keyBenefits = [
  {
    icon: TrendingUp,
    title: "Improved Operational Efficiency",
    description:
      "Automate routine tasks and streamline equipment operations to support smoother workflows.",
  },
  {
    icon: Gauge,
    title: "Consistent Process Control",
    description:
      "Use automated controls to maintain more consistent machine operation and reduce manual errors.",
  },
  {
    icon: Eye,
    title: "Better System Visibility",
    description:
      "Access useful operating data to understand equipment status and identify issues that need attention.",
  },
  {
    icon: Shield,
    title: "Reduced Operational Disruptions",
    description:
      "Improve fault detection and troubleshooting to help teams respond to equipment problems.",
  },
  {
    icon: SlidersHorizontal,
    title: "Flexible System Configuration",
    description:
      "Adapt automated electrical systems to change equipment requirements and operational needs.",
  },
  {
    icon: BarChart3,
    title: "Informed Decision-Making",
    description:
      "Use control system data to evaluate performance and identify opportunities for improvement.",
  },
];

const investmentFactors = [
  {
    icon: Plug,
    title: "Existing Electrical Infrastructure",
    description:
      "Review the current electrical panels, wiring, controllers, and connected equipment to understand what can be retained, upgraded, or replaced. This assessment helps establish a suitable starting point for the project.",
  },
  {
    icon: ClipboardList,
    title: "Operational Requirements",
    description:
      "Identify the tasks that need automation, the equipment involved, and the level of control required. Clear requirements help ensure that the proposed automated electrical systems support actual operating needs.",
  },
  {
    icon: Network,
    title: "Equipment and System Compatibility",
    description:
      "Check whether existing machinery, control devices, and software can communicate with the proposed automation technology. Compatibility considerations help identify integration requirements before implementation begins.",
  },
  {
    icon: Wallet,
    title: "Project Scope and Budget",
    description:
      "Define the work involved, required components, installation needs, and expected project outcomes. A clearly established scope helps businesses assess costs, prioritise requirements, and plan resources more effectively.",
  },
  {
    icon: Maximize2,
    title: "Future Expansion Plans",
    description:
      "Consider possible changes in production capacity, facility layout, or equipment requirements. Planning for future needs can help avoid unnecessary redesign when operations expand or new technologies are introduced.",
  },
  {
    icon: ShieldCheck,
    title: "Safety and Compliance Requirements",
    description:
      "Identify the electrical protection measures, machinery safety functions, and applicable standards relevant to the facility. Addressing these requirements during planning helps support safe system design and reliable operation.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Site Assessment",
    description:
      "We evaluate existing systems, workflows, and infrastructure to identify gaps and improvement opportunities. This ensures stronger planning, better efficiency, and long-term operational stability with accurate data collection.",
  },
  {
    step: "02",
    title: "System Design",
    description:
      "We create customized automation designs aligned with production goals, safety standards, and expansion needs. Our approach ensures scalability, performance optimization, and future-ready industrial system planning.",
  },
  {
    step: "03",
    title: "Equipment Installation",
    description:
      "We install control panels, wiring, and automation hardware with precision and safety compliance. This stage ensures complete electrical systems integration, minimizing downtime while maintaining consistent installation quality.",
  },
  {
    step: "04",
    title: "Programming Setup",
    description:
      "As a programming company, we configure PLCs, HMIs, and control systems for reliable operation. This improves machine responsiveness, and ensures accurate, efficient, and reliable system control logic execution.",
  },
  {
    step: "05",
    title: "Testing and Commissioning",
    description:
      "We conduct system validation and performance checks before deployment. This ensures electrical integration and commissioning is accurate, safe, and optimized, while confirming all electrical automation solutions function as intended.",
  },
  {
    step: "06",
    title: "Ongoing Support",
    description:
      "We provide continuous monitoring, troubleshooting, and optimization services after project completion. This ensures integrated electrical solutions remain reliable, efficient, and adaptable to evolving industrial demands and operational changes.",
  },
];

const whyChoose = [
  {
    icon: Layers,
    title: "Single-Source Execution",
    description:
      "We manage integrated electrical services from design to commissioning under one team improving coordination and reducing delays.",
  },
  {
    icon: Cpu,
    title: "Industrial Expertise",
    description:
      "As a trusted industrial electrical contractor, we understand manufacturing logistics and large-scale industrial facility demands.",
  },
  {
    icon: Shield,
    title: "Reliable Project Delivery",
    description:
      "Our team executes electrical systems integration projects safely and on schedule ensuring dependable long-term performance.",
  },
  {
    icon: Gauge,
    title: "Scalable Electrical Automation Solutions",
    description:
      "We build flexible electrical automation solutions that scale with operations and support future expansion needs.",
  },
];

const services = [
  {
    icon: Cpu,
    title: "PLC Programming",
    description:
      "We deliver advanced PLC programming to improve machine control, accuracy, and uptime. Our electrical automation services reduce manual effort and enhance reliability, including real-time diagnostics and faster response optimization.",
  },
  {
    icon: Settings,
    title: "Control Panels",
    description:
      "We design custom control panels that ensure safe operation and consistent performance. Our integrated electrical services support installation, wiring coordination, and system reliability, including safety compliance and modular design.",
  },
  {
    icon: Activity,
    title: "SCADA Systems",
    description:
      "We design SCADA systems for real-time monitoring and operational visibility. Electrical systems integration ensures reliable data flow, improves decision-making, and supports predictive maintenance with live alerts.",
  },
  {
    icon: Wrench,
    title: "Conveyor Automation",
    description:
      "We build conveyor automation systems that improve material handling speed and accuracy. Our electrical automation solutions enhance throughput, reduce downtime, and support warehouse efficiency with smart routing controls.",
  },
  {
    icon: Gauge,
    title: "Motor Controls",
    description:
      "As a trusted electrical systems company, we provide motor control and VFD integration to improve energy use and equipment life. Our electrical automation services optimize performance, reduce maintenance needs, and ensure smooth operation.",
  },
  {
    icon: Network,
    title: "Systems Integration",
    description:
      "We unify electrical, mechanical, and control systems into one unified network. Electrical systems integration improves communication, reduces errors, and ensures consistent performance across industrial operations.",
  },
];

const integrationCapabilities = [
  {
    icon: Cpu,
    title: "PLC and HMI Compatibility",
    description:
      "Ensure programmable logic controllers (PLCs) and human-machine interfaces (HMIs) exchange commands and operating data correctly for effective machine control.",
  },
  {
    icon: Network,
    title: "Industrial Communication Protocols",
    description:
      "Select suitable protocols, such as Modbus, EtherNet/IP, and PROFINET, to support reliable communication between compatible industrial devices.",
  },
  {
    icon: Layers,
    title: "Multi-Vendor Equipment Integration",
    description:
      "Connect compatible machinery and control components from different manufacturers to improve coordination across industrial operations.",
  },
  {
    icon: Activity,
    title: "Integration With Industrial Software",
    description:
      "Link electrical controls with SCADA, warehouse control systems (WCS), and warehouse execution systems (WES) to support equipment coordination and operational visibility.",
  },
  {
    icon: Wrench,
    title: "Legacy Equipment Compatibility",
    description:
      "Evaluate existing machinery and available interfaces to identify practical ways to connect older equipment with modern automated electrical systems.",
  },
];

const performanceMetrics = [
  {
    icon: Gauge,
    title: "Equipment Downtime",
    description:
      "Track the frequency and duration of unplanned stoppages to assess whether automation improvements support more reliable equipment operation.",
  },
  {
    icon: TrendingUp,
    title: "Production Throughput and Accuracy",
    description:
      "Measure production volumes, processing times, and error rates to evaluate whether automated electrical systems meet operational targets consistently.",
  },
  {
    icon: Wrench,
    title: "Maintenance and Fault Response",
    description:
      "Review equipment alerts, fault frequency, and troubleshooting time to identify opportunities for better maintenance planning and faster issue resolution.",
  },
  {
    icon: Zap,
    title: "Energy and Resource Efficiency",
    description:
      "Monitor energy consumption, equipment operating hours, and resource usage to identify potential improvements in operational efficiency.",
  },
];

const industries = [
  { title: "Warehousing and Distribution", description: "We provide services that improve logistics efficiency and warehouse automation systems." },
  { title: "Manufacturing", description: "Our electrical automation solutions improve production efficiency and reduce operational downtime." },
  { title: "Food and Beverage", description: "We implement electrical systems integration that supports hygiene, compliance, and process control." },
  { title: "Pharmaceutical", description: "We deliver precision-driven integrated electrical solutions for regulated production environments." },
  { title: "Cold Storage", description: "We build reliable electrical automation services that support temperature-sensitive operations." },
  { title: "Automotive and Aerospace", description: "We support high-performance industries with advanced automation control systems." },
];

const faqs = [
  {
    q: "What components are included in your electrical automation services?",
    a: "Our services include PLC systems, HMIs, SCADA platforms, control panels, sensors, and industrial networking. We integrate these components to ensure stable communication, precise control, and efficient industrial operations.",
  },
  {
    q: "How do electrical automation solutions improve system efficiency?",
    a: "They reduce manual intervention by automating control logic, optimizing machine cycles, and enabling real-time monitoring. We engineer systems that improve throughput, reduce errors, and enhance energy efficiency.",
  },
  {
    q: "Which communication protocols are used in integrated electrical systems?",
    a: "We work with protocols like Modbus, Ethernet/IP, Profibus, and Profinet in integrated electrical services. These protocols ensure reliable data exchange between controllers, devices, and industrial equipment.",
  },
  {
    q: "How do you design industrial automation systems?",
    a: "As a controls company, we design control architecture using load analysis, signal flow mapping, and redundancy planning. This ensures stable operation, fault tolerance, and optimized machine control.",
  },
  {
    q: "Can electrical systems integration support predictive maintenance?",
    a: "Yes, it enables predictive maintenance by collecting real-time sensor data, analyzing equipment performance trends, and triggering alerts before system failures occur.",
  },
  {
    q: "Which industries can benefit from electrical automation services?",
    a: "Manufacturing, warehousing, distribution, food processing, pharmaceuticals, cold storage, and automotive facilities can benefit from electrical automation. The right solution depends on equipment requirements, operating conditions, and production goals.",
  },
  {
    q: "Can automated electrical systems be customised for specific facility requirements?",
    a: "Yes. Automated electrical systems can be designed around a facility's equipment, workflows, control requirements, and expansion plans. MTLI Group develops solutions based on project scope and operational needs.",
  },
  {
    q: "Can MTLI Group integrate new automation technology into an existing facility?",
    a: "Yes. Electrical Systems Integration can connect compatible existing equipment with new controllers, monitoring platforms, and automation technology. The available options depend on the current infrastructure and equipment compatibility.",
  },
  {
    q: "What information should I provide when requesting an electrical automation project estimate?",
    a: "Provide details about your facility, existing equipment, project objectives, required upgrades, and expected timeline. Equipment lists, electrical drawings, and process requirements can also help MTLI Group assess the scope and prepare a more informed estimate.",
  },
  {
    q: "How can electrical automation support warehouse material handling?",
    a: "Electrical automation can coordinate conveyor systems, sensors, motor controls, and compatible warehouse technologies. These connections help manage material movement, improve equipment coordination, and support consistent warehouse workflows.",
  },
];

const ElectricalServices = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <RegionalSEO
        title="Electrical Automation Services | MTLI Group"
        exactTitle
        description="Explore MTLI Group’s electrical automation services, including PLC programming, control panels, SCADA, and systems integration. Get expert support."
      />
      <SchemaMarkup
        schemas={[
          {
            type: "Service",
            name: "Electrical Automation Services",
            description:
              "Industrial electrical automation services including PLC programming, SCADA systems, control panels, motor controls, conveyor automation, and electrical systems integration.",
            provider: "MTLI Group of Companies",
            providerId: "https://mtligroup.com/#organization",
            url: "https://mtligroup.com/services/electrical-automation-services",
            areaServed: ["United States", "Canada"],
            serviceType: "Electrical Automation",
          },
          {
            type: "BreadcrumbList",
            items: [
              { name: "Home", url: "/" },
              { name: "Services", url: "/services" },
              { name: "Electrical Automation Services", url: "/services/electrical-automation-services" },
            ],
          },
          {
            type: "FAQPage",
            questions: faqs.map((f) => ({ question: f.q, answer: f.a })),
          },
        ]}
      />
      <Header />
      <main className="flex-grow">
        {/* Hero */}
        <section className="relative h-[70vh] min-h-[500px] flex items-center">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${electricalHero})` }}
          >
            <div className="absolute inset-0 bg-secondary/80" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex items-center gap-2 text-primary mb-4">
              <Link to="/services" className="hover:underline">Services</Link>
              <span className="text-white/60">/</span>
              <span className="text-white">Electrical Automation Services</span>
            </div>
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-lg bg-primary/20 flex items-center justify-center backdrop-blur-sm">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Electrical Automation Services
                </h1>
              </div>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                At MTLI Group, we deliver advanced electrical automation services designed to improve
                operational efficiency, reduce downtime, and support long-term industrial growth across
                the United States.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" asChild>
                  <Link to="/book-a-call?interest=Electrical">
                    Book a Call
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-secondary" asChild>
                  <Link to="/projects?category=electrical">View Our Work</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Conversion band: specialist call request with this service preselected */}
        <ServiceCallbackBand label="Electrical & Controls" interest="Electrical, Controls & Software" formKey="svc-electrical" />

        {/* Key Benefits of Electrical Automation Systems */}
        <section className="py-20 bg-muted border-t border-border">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
              <div>
                <img
                  src={electricalBenefitsImg}
                  alt="Technician working inside an open electrical control cabinet in an automated industrial facility"
                  loading="lazy"
                  decoding="async"
                  width={1024}
                  height={1280}
                  className="w-full h-[300px] sm:h-[420px] lg:h-full object-cover object-center rounded-2xl border border-border shadow-lg"
                />
              </div>
              <div>
                <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                  Benefits
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                  Key Benefits of Electrical Automation Systems
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Electrical automation helps industrial facilities manage equipment, control processes, and maintain consistent operations. Here are the advantages:
                </p>
                <ul className="space-y-4">
                  {keyBenefits.map((benefit) => (
                    <li key={benefit.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <benefit.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                        <p className="text-muted-foreground">{benefit.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-primary font-semibold uppercase tracking-wider text-sm">Why MTLI</span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">
                  Complete Electrical Automation Solutions
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  MTLI Group delivers advanced electrical automation services across the United States with a
                  fully integrated industrial approach. As a trusted electrical automation company, we combine
                  engineering, controls, and field execution into one reliable delivery system.
                </p>
                <p className="text-lg text-muted-foreground mb-8">
                  From planning to electrical systems integration, we manage every stage with precision and
                  accountability. Our solutions improve uptime, operational control, and production efficiency
                  for industrial facilities.
                </p>
                <ul className="space-y-3">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {valueProps.map((vp) => (
                  <div key={vp.title} className="bg-muted p-6 rounded-xl">
                    <vp.icon className="h-10 w-10 text-primary mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">{vp.title}</h3>
                    <p className="text-sm text-muted-foreground">{vp.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Investment Considerations */}
        <section className="py-20 bg-background border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                Investment
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                What to Consider Before Investing in Electrical Automation
              </h2>
              <p className="text-lg text-muted-foreground">
                Investing in electrical automation requires a clear understanding of your facility's current setup, operational challenges, and future requirements. Evaluating these factors before starting a project helps businesses choose suitable solutions, plan resources, and establish realistic project goals.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {investmentFactors.map((factor) => (
                <div
                  key={factor.title}
                  className="bg-muted/40 border border-border rounded-xl p-6 h-full flex flex-col transition-colors hover:border-primary/40"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <factor.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{factor.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{factor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Our Process</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                A Structured Path to Reliable Automation
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We follow a structured process to deliver high-quality electrical automation services across every project.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processSteps.map((item) => (
                <div key={item.step} className="bg-background p-6 rounded-xl relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <span className="absolute -top-4 -right-4 text-8xl font-bold text-muted opacity-50 group-hover:text-primary/20 transition-colors">
                    {item.step}
                  </span>
                  <div className="relative">
                    <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">The MTLI Advantage</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                Why Choose MTLI Group for Electrical Automation Services?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                As a dependable controls company, we deliver services backed by engineering expertise and proven
                industrial experience across the United States.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChoose.map((item) => (
                <div key={item.title} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Solutions</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                Our Electrical Automation Services
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We deliver high-performance solutions designed for industrial efficiency, safety, and scalability across complex operations.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="bg-background p-6 rounded-xl border border-transparent hover:border-primary/20 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="bg-secondary rounded-2xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                <Zap className="w-full h-full" />
              </div>
              <div className="relative z-10 max-w-3xl">
                <span className="text-primary font-semibold uppercase tracking-wider text-sm">Case Study</span>
                <h3 className="text-2xl md:text-3xl font-bold text-white mt-2 mb-4">
                  Full Electrical Automation Retrofit, National Distribution Hub
                </h3>
                <p className="text-white/80 mb-6 text-lg">
                  "MTLI Group delivered a complete PLC, SCADA, and conveyor controls upgrade across our facility
                  with zero unplanned downtime. Their integrated team handled design, panel build, and commissioning
                  under one schedule and delivered measurable throughput gains."
                </p>
                <p className="text-white font-semibold">,  VP of Operations, National 3PL</p>
                <div className="mt-8">
                  <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-secondary" asChild>
                    <Link to="/projects">View More Projects</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Control System Compatibility and Equipment Integration */}
        <section className="py-20 bg-background border-t border-border">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
              <div>
                <img
                  src={integrationImg}
                  alt="Engineers reviewing control panels and integration workstations alongside automated equipment in an industrial facility"
                  loading="lazy"
                  decoding="async"
                  width={1024}
                  height={581}
                  className="w-full h-[300px] sm:h-[420px] lg:h-full object-cover object-center rounded-2xl border border-border shadow-lg"
                />
              </div>
              <div>
                <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                  Integration
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                  Control System Compatibility and Equipment Integration
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Industrial facilities often use equipment from different manufacturers, each with its own control and communication requirements. MTLI Group provides Integrated Electrical Solutions that help compatible equipment, control systems, and software work together to support coordinated industrial operations.
                </p>
                <ul className="space-y-4">
                  {integrationCapabilities.map((item) => (
                    <li key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Performance Measurement */}
        <section className="py-20 bg-background border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                Performance
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                Measuring the Performance of Electrical Automation Projects
              </h2>
              <p className="text-lg text-muted-foreground">
                Evaluating electrical automation against clear operational goals helps businesses understand system performance and identify areas for improvement. MTLI Group provides electrical automation services designed around project requirements and measurable operational objectives.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {performanceMetrics.map((metric) => (
                <div
                  key={metric.title}
                  className="bg-muted/40 border border-border rounded-xl p-6 h-full flex flex-col transition-colors hover:border-primary/40"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <metric.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{metric.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Industries</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                Industries We Serve
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We deliver electrical automation services across multiple industries.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((industry) => (
                <div key={industry.title} className="bg-background p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{industry.title}</h3>
                  <p className="text-muted-foreground">{industry.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-primary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Get Expert Electrical Automation Support Today!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Upgrade your operations with MTLI Group's advanced electrical automation services designed for performance,
              reliability, and long-term growth. We help you build smarter, safer, and more efficient industrial systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/book-a-call?interest=Electrical">Book a Call</Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-primary" asChild>
                <Link to="/projects?category=electrical">View Our Projects</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-12">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">FAQ</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((item, idx) => (
                <AccordionItem
                  key={item.q}
                  value={`faq-${idx}`}
                  className="bg-background rounded-xl border border-border px-6"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <SoftwareControls variant="service" />
        <InternalLinks currentType="service" currentHref="/services/electrical-automation-services" />
      </main>
      <Footer />
    </div>
  );
};

export default ElectricalServices;
